// @flow
import React, { useState, useEffect } from 'react';
import rtlDetect from 'rtl-detect';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';

import List from '@material-ui/core/List';
import Radio from '@material-ui/core/Radio';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';

import withMobileDialog from '@material-ui/core/withMobileDialog';

import { getSupportedLocalesByApp } from 'constants/languages';
import { useMutationSaveRetailerPreferences } from 'containers/useMutationSaveRetailerPreferences';

import { RTL } from '../constants/locale';
import { CLOSE, SELECT_YOUR_LANGUAGE } from 'constants/strings';
import type { Node } from 'react';

const LOCALES = getSupportedLocalesByApp();

type Value = {
  language: string,
  direction: 'ltr' | 'rtl',
  setLocale: string => void,
  openDialoag: boolean => void,
};

const defaultValues = {
  language: 'en',
  direction: 'ltr',
  setLocale: () => {},
  openDialoag: () => {},
};

const ResponsiveDialog = withMobileDialog()(Dialog);

export const LocaleContext = React.createContext<Value>(defaultValues);

export const LocaleContextProvider = ({ children }: { children: Node }) => {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState(
    localStorage.getItem('language') || 'en'
  );
  const [direction, setDirection] = useState(
    rtlDetect.getLangDir(language) || 'ltr'
  );

  useEffect(() => {
    if (direction && direction === RTL) {
      document.body.setAttribute('dir', RTL);
    } else {
      document.body.removeAttribute('dir');
    }
  }, [direction]);

  const [onSaveRetailerPreferences] = useMutationSaveRetailerPreferences({
    onCompleted: (data) => {
    },
    onError: (error) => {
      console.error("Error when saving the retailer preferences", error);
    },
  });

  const handleSetLocale = (lang: string) => {
    if (!lang) return;

    updateLanguage(lang);
    if(localStorage.getItem('token')) {
      onSaveRetailerPreferences({
        variables: {
          retailerPreferencesInput: {
            language: lang
          }
        }
      });
    }
  };

  const updateLanguage = (lang) => {
    const newDirection = rtlDetect.getLangDir(lang);
    localStorage.setItem('language', lang);
    setLanguage(lang);
    if (direction !== newDirection) {
      setDirection(newDirection || 'ltr');
      newDirection === RTL
        ? document.body.setAttribute('dir', RTL)
        : document.body.removeAttribute('dir');
      window.location.reload();
    }
    setOpen(false);
  }

  return (
    <LocaleContext.Provider
      value={{
        language,
        direction,
        setLocale: handleSetLocale,
        openDialoag: setOpen,
        updateLanguage
      }}
    >
      {children}
      <ResponsiveDialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="language-selector-dialog"
      >
        <DialogTitle id="language-selector-dialog">
          {SELECT_YOUR_LANGUAGE}
        </DialogTitle>
        <DialogContent>
          <List>
            {LOCALES.map(arr => (
              <ListItem
                key={arr[0]}
                button
                divider
                onClick={() => handleSetLocale(arr[0])}
              >
                <ListItemText primary={arr[1]} />
                <Radio
                  checked={arr[0] === language}
                  value={arr[0]}
                  name="radio-language"
                  aria-label={arr[2]}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setOpen(false)}>
            {CLOSE}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </LocaleContext.Provider>
  );
};
